import type { Route } from "./+types/terms";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale } from "~/i18n/index";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  return [
    { title: `Terms of Service — Caratzon` },
    { name: "description", content: "Caratzon Terms of Service governing use of the platform, trade catalog, IP licensing, and designer services." },
    ...socialMeta({ title: "Terms of Service — Caratzon", description: "Terms governing use of the Caratzon platform.", pathname: "/terms", locale }),
  ];
}

const EFFECTIVE_DATE = "March 4, 2026";

const sectionHeadingStyle = {
  fontFamily: "var(--font-display)",
  fontWeight: 300,
  fontSize: "clamp(1.3rem, 2.2vw, 1.7rem)",
  lineHeight: 1.25,
  letterSpacing: "0.01em",
} as const;

const subHeadingStyle = {
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: "0.85rem",
  letterSpacing: "0.02em",
} as const;

const bodyStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.82rem",
  lineHeight: 1.85,
  color: "var(--muted-foreground)",
} as const;

const LICENSE_TIERS = [
  { tier: "Non-Exclusive", exclusivity: "Multiple brands may purchase", mfgLimit: "Uncapped", ipTransfer: "No" },
  { tier: "Limited Run", exclusivity: "Non-exclusive", mfgLimit: "Capped at a specific unit volume", ipTransfer: "No" },
  { tier: "Exclusive", exclusivity: "Restricted to a single brand", mfgLimit: "Uncapped", ipTransfer: "No (design removed from marketplace)" },
  { tier: "Full Buyout", exclusivity: "Restricted to a single brand", mfgLimit: "Uncapped", ipTransfer: "Yes (complete assignment of IP to brand)" },
];

const thStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.68rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "var(--muted-foreground)",
  padding: "0.6rem 0.75rem",
  textAlign: "left" as const,
  borderBottom: "1px solid var(--border)",
};

const tdStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.78rem",
  lineHeight: 1.6,
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid var(--border)",
  color: "var(--muted-foreground)",
};

export default function TermsOfServiceRoute() {
  return (
    <MarketingLayout>
      <div className="px-6 lg:px-12 pt-20 pb-24 max-w-3xl mx-auto">
        {/* Header */}
        <p
          className="issue-label mb-4"
          style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}
        >
          Legal
        </p>
        <h1
          className="font-display font-light text-foreground mb-3"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.1, letterSpacing: "0.01em" }}
        >
          Terms of Service
        </h1>
        <p style={{ ...bodyStyle, fontStyle: "italic", marginBottom: "3rem" }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="space-y-10">

          {/* ── 1. Acceptance ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>1. Acceptance of Terms</h2>
            <p style={bodyStyle}>
              By accessing or using the Caratzon platform ("Platform"), including the website at caratzon.com, the trade catalog, designer studio, and all related services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use the Platform.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              These Terms constitute a legally binding agreement between you ("User," "you") and Clean Jewel Beyond Limited ("Caratzon," "we," "us"), a company incorporated in Hong Kong operating the Caratzon platform. We may update these Terms from time to time; continued use after changes constitutes acceptance.
            </p>
          </section>

          {/* ── 2. Platform Description ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>2. Platform Description</h2>
            <p style={bodyStyle}>
              Caratzon is a B2B2C jewelry platform connecting independent jewelry designers, retail brands, and vetted manufacturers. The Platform provides:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li>A curated design catalog and Inspire discovery experience</li>
              <li>3D and AI-enhanced design browsing and story presentation</li>
              <li>IP licensing and acquisition workflows across multiple tiers</li>
              <li>Customization request and order management</li>
              <li>Manufacturer routing and production fulfillment</li>
              <li>Stripe-powered payment processing</li>
            </ul>
          </section>

          {/* ── 3. Account Registration ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>3. Account Registration</h2>
            <p style={bodyStyle}>
              To access trade features, you must create an account and provide accurate, current information. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Accounts are categorized by role: Designer, B2B Buyer (Brand), Manufacturer, or Production Manager. Each role grants access to specific zones of the Platform. Misrepresentation of your role or business identity may result in immediate account termination.
            </p>
          </section>

          {/* ── 4. Intellectual Property & Platform Display Rights ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>4. Intellectual Property &amp; Platform Display Rights</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>4.1 Designer Retention of Rights</h3>
            <p style={bodyStyle}>
              Except for the specific licenses granted herein, the Designer retains all right, title, and interest, including all global copyrights, in and to the original Designs uploaded to the Caratzon platform.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>4.2 Platform Operations License</h3>
            <p style={bodyStyle}>
              By uploading a Design, the Designer grants Caratzon a non-exclusive, worldwide, royalty-free, revocable license to host, display, reproduce, modify (solely for formatting purposes), and distribute the Design across the Platform and related marketing channels.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>4.3 CAD File Protection</h3>
            <p style={bodyStyle}>
              Production-grade CAD files are never exposed to buyers. Only Caratzon-vetted manufacturers may access production CAD under strict NDA terms. Viewer-facing 3D renders use time-limited, signed URLs that cannot be redistributed.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>4.4 Platform Content</h3>
            <p style={bodyStyle}>
              All Platform content — including UI design, branding, AI-generated stories, lookbook images, and editorial copy — is owned by Caratzon or its licensors. You may not reproduce, distribute, or create derivative works from Platform content without written permission.
            </p>
          </section>

          {/* ── 5. Commercial Licensing Framework ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>5. Commercial Licensing Framework</h2>
            <p style={bodyStyle}>
              The Platform offers a tiered licensing structure that defines the scope of rights a purchasing Brand receives. Each license tier carries distinct terms regarding exclusivity, manufacturing volume, and intellectual property transfer.
            </p>

            <h3 className="text-foreground mt-5 mb-3" style={subHeadingStyle}>5.1 License Tier Matrix</h3>
            <div className="overflow-x-auto mt-3 rounded-lg border border-border">
              <table className="w-full" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--muted)" }}>
                    <th style={thStyle}>License Tier</th>
                    <th style={thStyle}>Brand Exclusivity</th>
                    <th style={thStyle}>Manufacturing Limit</th>
                    <th style={thStyle}>IP Ownership Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {LICENSE_TIERS.map((row) => (
                    <tr key={row.tier}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "var(--foreground)" }}>{row.tier}</td>
                      <td style={tdStyle}>{row.exclusivity}</td>
                      <td style={tdStyle}>{row.mfgLimit}</td>
                      <td style={tdStyle}>{row.ipTransfer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-foreground mt-5 mb-2" style={subHeadingStyle}>5.2 Manufacturing Rights Grant</h3>
            <p style={bodyStyle}>
              Upon successful purchase of a License, the Designer grants the purchasing Brand the right to use, adapt, and manufacture physical goods based on the Design, strictly subject to the limitations of the purchased License Tier.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>5.3 Exclusive &amp; Full Buyout Terms</h3>
            <p style={bodyStyle}>
              For Exclusive licenses, the Design is removed from the active marketplace for the duration of the exclusivity period. For Full Buyout licenses, complete assignment of intellectual property is executed, and the Design is permanently retired from the Caratzon catalog.
            </p>
          </section>

          {/* ── 6. Manufacturing, Liability & Quality Control ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>6. Manufacturing, Liability &amp; Quality Control</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>6.1 Mandatory Prototyping &amp; Sign-Off</h3>
            <p style={bodyStyle}>
              Prior to mass production, Brands must review and approve a physical or high-fidelity digital prototype. By providing written approval of the prototype, the Brand accepts the design specifications, and Caratzon shall not be liable for aesthetic or design-related claims on the final manufactured goods.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>6.2 Defect Tolerance</h3>
            <p style={bodyStyle}>
              Caratzon adheres to industry-standard manufacturing practices. The Brand acknowledges that a minor defect rate of up to three percent (3%) is standard in physical jewelry manufacturing. Refunds or replacements will only be issued for defective units exceeding this standard tolerance threshold.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>6.3 Platform Manufacturing Requirement</h3>
            <p style={bodyStyle}>
              Unless a "Design-Only" file license is explicitly purchased, all manufacturing rights granted on the Caratzon platform must be fulfilled exclusively through Caratzon's authorized manufacturing network. Brands may not use licensed designs to commission production through unauthorized third-party manufacturers.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>6.4 Manufacturer Routing</h3>
            <p style={bodyStyle}>
              Caratzon routes production to vetted manufacturing partners based on capability, capacity, and geographic proximity. While we exercise due diligence in partner selection, Caratzon acts as a facilitator and is not the direct manufacturer of physical goods.
            </p>
          </section>

          {/* ── 7. Warranties of Originality & Indemnification ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>7. Warranties of Originality &amp; Indemnification</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>7.1 Designer Warranty of Originality</h3>
            <p style={bodyStyle}>
              The Designer represents and warrants that all uploaded Designs are their sole, original creation; do not infringe upon any third-party copyrights, trademarks, patents, or trade secrets; and do not contain unauthorized assets generated by Artificial Intelligence.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>7.2 Total Indemnification</h3>
            <p style={bodyStyle}>
              The Designer agrees to fully indemnify, defend, and hold harmless Caratzon and any purchasing Brands from any claims, damages, legal fees, or lost manufacturing costs arising from a breach of the Warranty of Originality or any third-party claims of intellectual property infringement.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>7.3 Brand Indemnification</h3>
            <p style={bodyStyle}>
              Brands agree to indemnify and hold harmless Caratzon, its officers, directors, employees, and agents from any claims, damages, or expenses (including reasonable legal fees) arising from the Brand's use of licensed Designs outside the scope of the purchased License Tier, or from the Brand's violation of these Terms.
            </p>
          </section>

          {/* ── 8. Confidentiality & NDAs ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>8. Confidentiality &amp; Non-Disclosure</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>8.1 Mutual Confidentiality</h3>
            <p style={bodyStyle}>
              During the prototyping and manufacturing process, both the Designer and Caratzon agree to maintain strict confidentiality regarding the Brand's intent to manufacture the Design, the timeline of production, and any associated Brand marks. All non-public information exchanged through the Platform — including pricing terms, production specifications, CAD files, and business communications — is considered confidential.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>8.2 Embargo on Designer Promotion</h3>
            <p style={bodyStyle}>
              For Exclusive and Full Buyout licenses, the Designer agrees not to publicly claim, feature in a portfolio, or disclose the licensing of the Design until the Brand has officially launched the manufactured product to the public, or until written permission is granted by the Brand.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>8.3 Platform Confidentiality</h3>
            <p style={bodyStyle}>
              Users may not disclose confidential information obtained through the Platform to third parties without the disclosing party's written consent. This obligation survives termination of these Terms.
            </p>
          </section>

          {/* ── 9. Financials, Fees & Royalties ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>9. Financials, Fees &amp; Royalties</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>9.1 Fee Structure</h3>
            <p style={bodyStyle}>
              Caratzon facilitates the transaction between Designer and Brand. Caratzon will deduct its platform commission, manufacturing costs, and applicable service fees prior to remitting the final license payout or royalty to the Designer. The specific fee schedule is disclosed during the transaction process and in the Designer's account dashboard.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>9.2 Trade Catalog Pricing</h3>
            <p style={bodyStyle}>
              Prices displayed in the trade catalog represent guide pricing set by Caratzon in consultation with designers. Final pricing — including manufacturing trade price, IP licensing fees per tier, and any customization surcharges — is confirmed during the order negotiation process.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              All prices are quoted in US Dollars (USD) unless otherwise stated. Caratzon reserves the right to adjust catalog pricing, minimum order quantities, and availability windows at any time.
            </p>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>9.3 Payment Processing</h3>
            <p style={bodyStyle}>
              Payment processing is handled through Stripe Connect. By using the Platform's payment features, you also agree to Stripe's{" "}
              <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">Terms of Service</a>.
              Designer payouts, manufacturer payments, and buyer charges are processed according to the terms of each transaction.
            </p>
          </section>

          {/* ── 10. Orders & Fulfillment ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>10. Orders &amp; Fulfillment</h2>
            <p style={bodyStyle}>
              Submitting a customization request or IP licensing enquiry does not constitute a binding order. Orders become binding only after both parties confirm terms through the Platform's workflow and payment is processed.
            </p>
          </section>

          {/* ── 11. Prohibited Conduct ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>11. Prohibited Conduct</h2>
            <p style={bodyStyle}>You agree not to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li>Upload designs that infringe third-party intellectual property rights</li>
              <li>Circumvent the Platform to transact directly with parties discovered through Caratzon</li>
              <li>Commission production of licensed designs through unauthorized manufacturers</li>
              <li>Scrape, crawl, or systematically extract data from the Platform</li>
              <li>Reverse-engineer 3D renders or AI-generated content to reconstruct CAD files</li>
              <li>Misrepresent your identity, business credentials, or role</li>
              <li>Use the Platform for any unlawful purpose or in violation of applicable trade regulations</li>
              <li>Redistribute signed asset URLs or attempt to bypass access controls</li>
              <li>Exceed the manufacturing volume or exclusivity scope of your purchased License Tier</li>
            </ul>
          </section>

          {/* ── 12. AI-Generated Content ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>12. AI-Generated Content</h2>
            <p style={bodyStyle}>
              The Platform uses artificial intelligence to generate design stories, lookbook imagery, catalog enhancements, and other editorial content. AI-generated content is provided for presentation purposes and may not perfectly represent the physical product. Buyers should rely on verified specifications, CAD data, and manufacturer confirmations for production decisions.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Designers warrant that their uploaded Designs do not contain unauthorized AI-generated assets. AI tools provided by the Platform (story generation, lookbook creation) produce derivative editorial content owned by Caratzon for platform display purposes.
            </p>
          </section>

          {/* ── 13. Limitation of Liability ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>13. Limitation of Liability</h2>
            <p style={bodyStyle}>
              To the maximum extent permitted by law, Caratzon's total liability for any claim arising from or related to the Platform shall not exceed the fees paid by you to Caratzon in the twelve (12) months preceding the claim. Caratzon is not liable for indirect, incidental, consequential, or punitive damages, including lost profits or business interruption.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Caratzon's liability for manufacturing-related claims is limited to facilitating resolution between the Brand and the manufacturing partner. Caratzon shall not be liable for aesthetic or design-related claims on manufactured goods for which the Brand has provided written prototype approval.
            </p>
          </section>

          {/* ── 14. Termination ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>14. Termination</h2>
            <p style={bodyStyle}>
              Caratzon may suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion, with or without notice. Upon termination, your right to use the Platform ceases immediately. Provisions that by their nature should survive termination — including IP ownership, confidentiality, limitation of liability, indemnification, and any active license agreements — shall survive.
            </p>
          </section>

          {/* ── 15. Dispute Resolution ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>15. Dispute Resolution</h2>
            <p style={bodyStyle}>
              Any dispute arising from these Terms or your use of the Platform shall first be submitted to good-faith mediation. If mediation fails, disputes shall be resolved by binding arbitration under the rules of the Hong Kong International Arbitration Centre (HKIAC). The seat of arbitration shall be Hong Kong. The language of arbitration shall be English.
            </p>
          </section>

          {/* ── 16. Governing Law ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>16. Governing Law</h2>
            <p style={bodyStyle}>
              These Terms are governed by and construed in accordance with the laws of the Hong Kong Special Administrative Region, without regard to conflict of law principles.
            </p>
          </section>

          {/* ── 17. Severability ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>17. Severability</h2>
            <p style={bodyStyle}>
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* ── 18. Contact ── */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>18. Contact</h2>
            <p style={bodyStyle}>
              For questions about these Terms, contact us at{" "}
              <a href="mailto:legal@caratzon.com" className="underline hover:text-foreground transition-colors">legal@caratzon.com</a>.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Clean Jewel Beyond Limited<br />
              Hong Kong
            </p>
          </section>
        </div>
      </div>
    </MarketingLayout>
  );
}
